import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useAuth } from '../contexts/AuthContext';

export interface Tenant {
  id: number;
  schemaName: string;
}

export interface SchoolData {
  id?: number;
  nameofSchool?: string;
  schoolCode?: string;
  diseCode?: string;
  jsk_aeo?: string;
  blockCode?: string;
  blockName?: string;
  districtCode?: string;
  district?: string;
  pinNo?: string;
  state?: string;
  schoolPrincipalName?: string;
  principalDesignation?: string;
  principalMobile?: string;
  principalEmail?: string;
  medium?: string[];
  schoolAdminIds?: number[];
  schoolLogo?: string;
  schoolImage?: string;
  active?: boolean;
  adminId?: number;
  emailServiceEnabled?: boolean;
  whatsappServiceEnabled?: boolean;
  websiteServiceEnabled?: boolean;
  feePaymentService?: boolean;
  customWebsiteUrl?: string;
  createdAt?: string;
  updatedAt?: string;
  tenantId?: number;
  tenant?: TenantDto;
  status?: boolean;
}

export interface TenantDto {
  id: number;
  schemaName: string;
  tenantId: string;
  name: string;
  contactEmail: string;
  active: boolean;
}

export interface SchoolWebsiteData {
  id?: number;
  schoolId?: number;
  title?: string;
  description?: string;
  heroImage?: string;
  aboutSection?: string;
  contactInfo?: {
    address?: string;
    phone?: string;
    email?: string;
    website?: string;
  };
  features?: string[];
  galleryImages?: string[];
  customCss?: string;
  customJs?: string;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface SchoolAdmin {
  id: number;
  username: string;
  email: string;
  fullName: string;
  schoolId: number | null;
  roles: string[];
  status?: boolean;
}

const API_URL = `${process.env.REACT_APP_API_URL}/schools`;

// Fetch all schools
const getAllSchools = async (): Promise<SchoolData[]> => {
  const response = await axios.get(API_URL);
  return response.data;
};

// Create a new school
const createSchool = async (schoolData: SchoolData, imageFile?: File, schoolImageFile?: File): Promise<SchoolData> => {
  const formData = new FormData();
  formData.append('schoolDto', JSON.stringify(schoolData));
  if (imageFile) {
    formData.append('image', imageFile);
  }
  if (schoolImageFile) {
    formData.append('schoolImage', schoolImageFile);
  }
  const response = await axios.post(API_URL, formData);
  return response.data;
};

// Update an existing school
const updateSchool = async (id: number, schoolData: SchoolData, imageFile?: File, schoolImageFile?: File): Promise<SchoolData> => {
  const formData = new FormData();
  formData.append('schoolDto', JSON.stringify(schoolData));
  if (imageFile) {
    formData.append('image', imageFile);
  }
  if (schoolImageFile) {
    formData.append('schoolImage', schoolImageFile);
  }
  const response = await axios.put(`${API_URL}/${id}`, formData);
  return response.data;
};

// Delete a school
const deleteSchool = async (id: number): Promise<void> => {
  await axios.delete(`${API_URL}/${id}`);
};

const fetchSchoolAdmins = async (): Promise<SchoolAdmin[]> => {
  const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/users?role=SCHOOL_ADMIN`);
  // Clean user data to remove duplicate 'role' field
  const cleanedAdmins = response.data.map((admin: any) => {
    const { role, ...cleanAdmin } = admin; // Remove duplicate 'role' field
    return cleanAdmin;
  });
  return cleanedAdmins;
};

// Fetch school logo info by school ID
const getSchoolLogoInfo = async (id: number): Promise<{ fileName: string; filePath: string }> => {
  const response = await axios.get(`${API_URL}/${id}/logo-info`);
  return response.data;
};

const getSchoolImageInfo = async (id: number): Promise<{ fileName: string; filePath: string }> => {
  const response = await axios.get(`${API_URL}/${id}/school-image-info`);
  return response.data;
};

// Fetch school by ID
const getSchoolById = async (id: number): Promise<SchoolData> => {
  const response = await axios.get(`${API_URL}/${id}`);
  return response.data;
};

export function useSchools(tenantLoading: boolean = false, options?: { enableQuery?: boolean; disableSchoolAdmins?: boolean }) {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  // Only enable the main schools query if explicitly requested
  // This prevents unnecessary API calls when components only need helper functions
  const shouldEnableQuery = options?.enableQuery ?? false;
  const isAppUser = user?.roles?.includes('APP_USER');

  const schoolsQuery = useQuery<SchoolData[], Error>({
    queryKey: ["schools"],
    queryFn: getAllSchools,
    enabled: !!user && shouldEnableQuery && !isAppUser,
    staleTime: 5 * 60 * 1000, // 5 minutes - data stays fresh for 5 minutes
    cacheTime: 30 * 60 * 1000, // 30 minutes - keep in cache
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
    refetchOnMount: false, // Use cached data instead of refetching on every mount
  });

  const createSchoolMutation = useMutation({
    mutationFn: ({ schoolData, imageFile, schoolImageFile }: { schoolData: SchoolData; imageFile?: File; schoolImageFile?: File }) =>
      createSchool(schoolData, imageFile, schoolImageFile),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["schools"] }),
  });

  const updateSchoolMutation = useMutation(
    (args: { id: number; schoolData: SchoolData; imageFile?: File; schoolImageFile?: File }) =>
      updateSchool(args.id, args.schoolData, args.imageFile, args.schoolImageFile),
    {
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries({ queryKey: ["schools"] });
        // Also invalidate the specific school by ID query to refresh Layout and ApplicationFormModal
        queryClient.invalidateQueries({ queryKey: ["schoolById", variables.id] });
        queryClient.invalidateQueries({ queryKey: ["users"] });
      },
    }
  );

  const deleteSchoolMutation = useMutation({
    mutationFn: deleteSchool,
    onMutate: async (deletedSchoolId) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["schools"] });

      // Snapshot the previous value
      const previousSchools = queryClient.getQueryData(["schools"]);

      // Optimistically update to the new value - remove the deleted school immediately
      queryClient.setQueryData(["schools"], (old: SchoolData[] | undefined) => {
        if (old) {
          return old.filter(school => school.id !== deletedSchoolId);
        }
        return old;
      });

      // Return a context object with the snapshotted value
      return { previousSchools };
    },
    onSuccess: () => {
      // Don't invalidate immediately - let the optimistic update persist
      // The data will be fresh on the next natural fetch or manual refresh
    },
    onError: (err, deletedSchoolId, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousSchools) {
        queryClient.setQueryData(["schools"], context.previousSchools);
      }
    }
    // Remove onSettled to prevent automatic refetching
  });

  // Check if user has roles that should not fetch school admins
  const isSchoolAdmin = user?.roles?.includes('SCHOOL_ADMIN');
  const isTeacher = user?.roles?.includes('TEACHER');
  const isStudent = user?.roles?.includes('STUDENT');
  // Default disableSchoolAdmins to true if not explicitly provided
  // This prevents unauthorized API calls from common components like Layout or Templates
  const disableSchoolAdminsValue = options?.disableSchoolAdmins !== undefined ? options.disableSchoolAdmins : true;
  const shouldFetchSchoolAdmins = disableSchoolAdminsValue ? false : (!!user && !isSchoolAdmin && !isTeacher && !isStudent && !isAppUser);

  const schoolAdminsQuery = useQuery<SchoolAdmin[]>({
    queryKey: ['schoolAdmins'],
    queryFn: fetchSchoolAdmins,
    enabled: shouldFetchSchoolAdmins,
    staleTime: Infinity, // Prevent refetching when disabled
    cacheTime: 0, // Don't cache when disabled
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    retry: false, // Don't retry when disabled
  });

  const useSchoolLogoInfo = (id?: number) =>
    useQuery<{ fileName: string; filePath: string }, Error>(
      ["schoolLogoInfo", id],
      () => getSchoolLogoInfo(id!),
      { enabled: !!id && !!user && !tenantLoading }
    );

  const useSchoolImageInfo = (id?: number) =>
    useQuery<{ fileName: string; filePath: string }, Error>(
      ["schoolImageInfo", id],
      () => getSchoolImageInfo(id!),
      { enabled: !!id && !!user && !tenantLoading }
    );

  // Add this hook for fetching a school by ID
  const useSchoolById = (id?: number) =>
    useQuery<SchoolData, Error>(
      ["schoolById", id],
      () => getSchoolById(id!),
      {
        enabled: !!id && !!user && !tenantLoading,
        staleTime: 5 * 60 * 1000, // 5 minutes - data stays fresh for 5 minutes
        cacheTime: 30 * 60 * 1000, // 30 minutes - keep in cache
        refetchOnWindowFocus: false, // Don't refetch when window regains focus
        refetchOnMount: false, // Use cached data instead of refetching on every mount
        refetchOnReconnect: false, // Don't refetch on reconnect
      }
    );

  return {
    schoolsQuery,
    createSchoolMutation,
    updateSchoolMutation,
    deleteSchoolMutation,
    schoolAdmins: schoolAdminsQuery.data || [],
    refetchSchoolAdmins: schoolAdminsQuery.refetch,
    getSchoolLogoInfo,
    useSchoolLogoInfo,
    getSchoolImageInfo,
    useSchoolImageInfo,
    useSchoolById,
  };
}
